import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useYoutubeSearch, { VIDEO_INTENTS } from '../hooks/useYoutubeSearch';
import LinkTitleNote from '../../../../components/Tracks/LinkTitleNote';
import useYoutubeLinkTitle from '../../../../components/Tracks/useYoutubeLinkTitle';
import { isYouTubeLink } from '../../../../services/TrackJobsService';
import { titleMatchesSong } from '../../../../services/videoTitleCheck';
import './VideoPicker.css';

/**
 * Pick the video for a song — search first, paste as the fallback.
 *
 * SEARCH IS THE PRIMARY PATH because it is what the feature is for (Anthony,
 * 2026-08-31: students bring lyric and karaoke videos). Paste stays visible
 * rather than hiding behind a toggle: it is the only path that works when the
 * daily quota is gone, and burying the fallback is how a teacher gets stuck
 * mid-lesson with no way forward.
 *
 * The default query is the song's own title/artist, so the common case is one
 * chip and one Search — nobody should have to retype what the board already
 * knows.
 *
 * LOOK BEFORE YOU SAVE (Anthony, 2026-09-28: "Increase visibility for YouTube
 * thumbnails when searching for options. Also add a preview option to make
 * search easier for teachers. Maybe a resizable window, even"). Results are a
 * grid of full-width 16:9 thumbnails (the server already sends YouTube's
 * 320x180 `medium` image; it was drawn at 72x40), and a tap PREVIEWS rather
 * than saves: the video plays in place, with **Use this video** to save it.
 * **Bigger** lifts the preview into a floating window the teacher can drag
 * larger from its corner, portalled to <body> so no column can clip it.
 */
// `autoSearch` (pass 4 S3, 2026-10-02): run the search as soon as the picker
// opens, for the add-a-song flow, where the teacher asked for nothing yet
// and a results grid is the whole point. `heading` replaces the line above.
const VideoPicker = ({ song, kind = 'lyrics', draft, setDraft, onPickUrl, onSaveDraft, onCancel, error, autoSearch = false, heading = null, cancelLabel = null }) => {
	const defaultQuery = [song?.metadata?.title || song?.name, song?.metadata?.artist]
		.filter(Boolean)
		.join(' ');

	const [query, setQuery] = useState(defaultQuery);
	// What the song is called, for the "is this the song?" checks below
	// (services/videoTitleCheck.js, 2026-10-22).
	const songName = song?.metadata?.title || song?.name || '';
	const draftInfo = useYoutubeLinkTitle(isYouTubeLink(draft) ? draft : null, songName);
	// Opens on the slot being filled: reaching here by clicking the empty
	// Karaoke chip and then having to select Karaoke again is a step that
	// carries no information.
	const [intent, setIntent] = useState(kind);
	const search = useYoutubeSearch();
	// The result being previewed, and whether it floats in the big window.
	const [preview, setPreview] = useState(null);
	const [big, setBig] = useState(false);

	const runSearch = (nextIntent = intent) => {
		setIntent(nextIntent);
		setPreview(null);
		setBig(false);
		search.search(query, nextIntent);
	};

	const closePreview = () => {
		setPreview(null);
		setBig(false);
	};

	useEffect(() => {
		if (autoSearch && query.trim().length >= 3) runSearch(kind);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Escape shrinks the big window first, then closes the preview.
	useEffect(() => {
		if (!preview) return undefined;
		const onKey = (e) => {
			if (e.key !== 'Escape') return;
			if (big) setBig(false);
			else setPreview(null);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [preview, big]);

	const kindLabel = VIDEO_INTENTS.find((o) => o.key === kind)?.label || 'video';

	return (
		<div className="ld-vpick">
			<p className="ld-vpick__head">{heading || <>Choosing the <strong>{kindLabel}</strong> video</>}</p>
			<div className="ld-vpick__row">
				<input
					type="search"
					className="ld-vpick__q"
					value={query}
					aria-label="Search for a video"
					placeholder="Song and artist…"
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && runSearch()}
				/>
				<button
					type="button"
					className="ld-vpick__go"
					disabled={search.isSearching || query.trim().length < 3}
					onClick={() => runSearch()}>
					{search.isSearching ? 'Searching…' : 'Search'}
				</button>
			</div>

			{/* Intent chips re-run the search on click rather than only setting a
			    filter: the intent IS the query, so a chip that changed state
			    silently would look broken until you pressed Search again. */}
			<div className="ld-vpick__chips" role="group" aria-label="What kind of video">
				{VIDEO_INTENTS.map((opt) => (
					<button
						key={opt.key}
						type="button"
						className={`ld-vpick__chip${intent === opt.key ? ' is-active' : ''}`}
						aria-pressed={intent === opt.key}
						disabled={search.isSearching}
						onClick={() => runSearch(opt.key)}>
						{opt.label}
					</button>
				))}
			</div>

			{search.error && <p className="ld-vpick__status ld-vpick__status--error">{search.error}</p>}
			{error && <p className="ld-vpick__status ld-vpick__status--error">{error}</p>}

			{search.searched && !search.isSearching && !search.results.length && !search.error && (
				<p className="ld-vpick__status">Nothing embeddable came back. Try another wording, or paste a link.</p>
			)}

			{preview && !big && (
				<VideoPreview result={preview} songName={songName} big={false} onUse={() => onPickUrl(preview.url)} onToggleBig={() => setBig(true)} onClose={closePreview} />
			)}
			{preview &&
				big &&
				typeof document !== 'undefined' &&
				createPortal(
					<div className="ld-vpick__overlay" role="dialog" aria-modal="true" aria-label={`Preview: ${preview.title}`}>
						<VideoPreview result={preview} songName={songName} big onUse={() => onPickUrl(preview.url)} onToggleBig={() => setBig(false)} onClose={closePreview} />
					</div>,
					document.body
				)}

			{!!search.results.length && (
				<ul className="ld-vpick__results">
					{search.results.map((r) => (
						<li key={r.videoId}>
							<button
								type="button"
								className={`ld-vpick__result${preview && preview.videoId === r.videoId ? ' is-previewing' : ''}`}
								aria-pressed={Boolean(preview && preview.videoId === r.videoId)}
								onClick={() => setPreview(r)}>
								<span className="ld-vpick__thumbwrap">
									{r.thumbnail ? <img src={r.thumbnail} alt="" className="ld-vpick__thumb" /> : <span className="ld-vpick__thumb ld-vpick__thumb--none" />}
									{r.duration && <span className="ld-vpick__dur">{r.duration}</span>}
									<span className="ld-vpick__play" aria-hidden="true">
										<i className="fa-sharp fa-solid fa-play" />
									</span>
								</span>
								<span className="ld-vpick__meta">
									{/* title carries the full string because the CSS
									    clamps it — a clipped value with no title is
									    simply gone (.ps-table--fit says so in its own
									    comment, and Today learned it the hard way). */}
									<span className="ld-vpick__title" title={r.title}>
										{r.title}
									</span>
									<span className="ld-vpick__sub">
										{r.channel}
										{r.duration ? ` · ${r.duration}` : ''}
									</span>
									{titleMatchesSong(r.title, songName) === false && (
										<span className="ld-vpick__differs">
											<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" /> Title does not name this song
										</span>
									)}
								</span>
							</button>
						</li>
					))}
				</ul>
			)}

			<div className="ld-vpick__paste">
				<input
					type="url"
					className="ld-vpick__q"
					value={draft}
					aria-label="Video link"
					placeholder="…or paste a link"
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && onSaveDraft()}
				/>
				<button type="button" className="ld-vpick__go" onClick={() => onSaveDraft()}>
					{draftInfo.mismatch ? 'Save anyway' : 'Save'}
				</button>
			</div>
			<LinkTitleNote info={draftInfo} songName={songName} />

			<button type="button" className="ld-vpick__cancel" onClick={onCancel}>
				{cancelLabel || 'Cancel'}
			</button>
		</div>
	);
};

/**
 * One result, playing. The id went through the server's validation and is
 * checked again here, since it lands in an iframe src.
 */
const VideoPreview = ({ result, songName = '', big, onUse, onToggleBig, onClose }) => {
	const differs = titleMatchesSong(result.title, songName) === false;
	const id = /^[A-Za-z0-9_-]{6,20}$/.test(String(result.videoId || '')) ? result.videoId : null;
	return (
		<div className={`ld-vpick__preview${big ? ' ld-vpick__preview--big' : ''}`}>
			<div className="ld-vpick__frame">
				{id ? (
					<iframe
						title={`Preview: ${result.title}`}
						src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
						allowFullScreen
					/>
				) : (
					<p className="ld-vpick__status">This video cannot be previewed here.</p>
				)}
			</div>
			<p className="ld-vpick__ptitle" title={result.title}>
				{result.title}
			</p>
			<p className="ld-vpick__sub">
				{result.channel}
				{result.duration ? ` · ${result.duration}` : ''}
			</p>
			{differs && (
				<p className="ld-vpick__differs ld-vpick__differs--block" role="status">
					<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" /> This title does not mention “{songName}”. Check it is the right song before you use it.
				</p>
			)}
			<div className="ld-vpick__pactions">
				<button type="button" className="ld-vpick__go" onClick={onUse}>
					{differs ? 'Use it anyway' : 'Use this video'}
				</button>
				<button type="button" className="ld-vpick__ghost" onClick={onToggleBig}>
					<i className={`fa-sharp fa-solid ${big ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" />
					{big ? 'Smaller' : 'Bigger'}
				</button>
				<button type="button" className="ld-vpick__ghost" onClick={onClose}>
					Back to results
				</button>
			</div>
			{big && <p className="ld-vpick__hint">Drag the bottom-right corner to resize.</p>}
		</div>
	);
};

export default VideoPicker;
